(() => {
  const KEY = "fvs_wishlist_v1";

  const sanitizeId = (id) => String(id || "").trim();

  const readRaw = () => {
    try {
      return JSON.parse(localStorage.getItem(KEY));
    } catch {
      return null;
    }
  };

  const normalizeState = (raw) => {
    if (Array.isArray(raw)) {
      return { ids: raw.map(sanitizeId).filter(Boolean), products: {} };
    }

    if (raw && typeof raw === "object") {
      const ids = Array.isArray(raw.ids) ? raw.ids.map(sanitizeId).filter(Boolean) : [];
      const products = raw.products && typeof raw.products === "object" ? raw.products : {};
      return { ids, products };
    }

    return { ids: [], products: {} };
  };

  const dedupe = (arr) => [...new Set((arr || []).map(sanitizeId).filter(Boolean))];

  const compactState = (rawState) => {
    const state = normalizeState(rawState);
    const ids = dedupe(state.ids);
    const products = {};

    ids.forEach((id) => {
      const product = state.products?.[id];
      if (product && typeof product === "object") {
        products[id] = {
          id,
          variantId: sanitizeId(product.variantId || product.variant_id),
          title: (product.title || "").toString().trim(),
          url: (product.url || "").toString().trim(),
          image: (product.image || "").toString().trim(),
          price: (product.price || "").toString().trim()
        };
      }
    });

    return { ids, products };
  };

  const readState = () => compactState(readRaw());

  const writeState = (state) => {
    const compacted = compactState(state);
    try {
      localStorage.setItem(KEY, JSON.stringify(compacted));
      return true;
    } catch {
      try {
        localStorage.setItem(KEY, JSON.stringify({ ids: compacted.ids, products: {} }));
        return true;
      } catch {
        return false;
      }
    }
  };

  const read = () => readState().ids;

  const has = (id) => readState().ids.includes(sanitizeId(id));
  let previousWishlistCount = readState().ids.length;

  const normalizeProductData = (id, input) => {
    if (!input) return null;
    const productId = sanitizeId(id);
    if (!productId) return null;

    const title = (input.title || "").toString().trim();
    const url = (input.url || "").toString().trim();
    const image = (input.image || "").toString().trim();
    const price = (input.price || "").toString().trim();
    const variantId = sanitizeId(input.variant_id || input.variantId);

    if (!title || !url) return null;

    return { id: productId, variantId, title, url, image, price };
  };

  const getProductDataFromButton = (btn) => {
    if (!btn) return null;
    return normalizeProductData(btn.dataset.productId, {
      variant_id: btn.dataset.productVariantId,
      title: btn.dataset.productTitle,
      url: btn.dataset.productUrl,
      image: btn.dataset.productImage,
      price: btn.dataset.productPrice
    });
  };

  const syncProductDataFromEmbeddedJson = () => {
    const state = readState();
    const wished = new Set(state.ids);
    if (!wished.size) return;
    let changed = false;

    document.querySelectorAll("[data-wishlist-product-json]").forEach((el) => {
      try {
        const parsed = JSON.parse(el.textContent || "{}");
        const product = normalizeProductData(parsed.id, parsed);
        if (!product || !wished.has(product.id)) return;
        state.products[product.id] = product;
        changed = true;
      } catch {
        // ignore invalid JSON blobs
      }
    });

    document.querySelectorAll("[data-wishlist-toggle]").forEach((btn) => {
      const product = getProductDataFromButton(btn);
      if (!product || !wished.has(product.id)) return;
      state.products[product.id] = product;
      changed = true;
    });

    if (changed) writeState(state);
  };

  const toggle = (id, productData = null) => {
    const productId = sanitizeId(id);
    if (!productId) return false;

    const state = readState();
    const alreadyExists = state.ids.includes(productId);

    if (alreadyExists) {
      state.ids = state.ids.filter((x) => x !== productId);
      delete state.products[productId];
    } else {
      state.ids.push(productId);
      const normalized = normalizeProductData(productId, productData);
      if (normalized) state.products[productId] = normalized;
    }

    const wrote = writeState(state);
    if (!wrote) return false;
    updateAllButtons();
    updateCount(true);
    renderWishlistPage();
    return !alreadyExists;
  };

  const add = (id, productData = null) => {
    const productId = sanitizeId(id);
    if (!productId) return false;

    const state = readState();
    const alreadyExists = state.ids.includes(productId);
    const normalized = normalizeProductData(productId, productData);

    if (!alreadyExists) state.ids.push(productId);
    if (normalized) state.products[productId] = normalized;

    const wrote = writeState(state);
    if (!wrote) return false;
    updateAllButtons();
    updateCount(!alreadyExists);
    renderWishlistPage();
    return true;
  };

  const updateAllButtons = () => {
    document.querySelectorAll("[data-wishlist-toggle]").forEach((btn) => {
      const id = btn.dataset.productId;
      const active = has(id);
      const isOverlayButton = btn.classList.contains("card-gallery__wishlist-button");

      btn.setAttribute("aria-pressed", active ? "true" : "false");
      btn.classList.toggle("is-active", active);

      const label = btn.querySelector("[data-wishlist-label]");
      if (label) label.textContent = active ? "Wishlisted" : isOverlayButton ? "Wishlist" : "Add to wishlist";

      btn.setAttribute("aria-label", active ? "Wishlisted" : "Add to wishlist");
    });
  };

  const animateWishlistBubble = (bubble) => {
    if (!bubble) return;
    const bubbleText = bubble.querySelector(".cart-bubble__text");
    if (!bubbleText) return;

    bubble.classList.remove("cart-bubble--animating");
    requestAnimationFrame(() => {
      bubble.classList.add("cart-bubble--animating");
      const clear = () => bubble.classList.remove("cart-bubble--animating");
      bubbleText.addEventListener("animationend", clear, { once: true });
      setTimeout(clear, 450);
    });
  };

  const updateCount = (animate = false) => {
    const count = readState().ids.length;
    const shouldAnimate = animate && count > 0 && count !== previousWishlistCount;

    document.querySelectorAll("[data-wishlist-count]").forEach((el) => {
      const hasItems = count > 0;
      const displayCount = count > 99 ? "99+" : String(count);
      el.textContent = hasItems ? displayCount : "";
      el.classList.toggle("hidden", !hasItems);

      const bubble = el.closest("[data-wishlist-bubble]");
      if (bubble) {
        bubble.classList.toggle("visually-hidden", !hasItems);
        bubble.toggleAttribute("data-maintain-ratio", count <= 99);
        if (shouldAnimate && hasItems) animateWishlistBubble(bubble);
      }

      const icon = el.closest(".header-actions__wishlist-icon");
      if (icon) {
        icon.classList.toggle("header-actions__wishlist-icon--has-items", hasItems);
      }
    });

    previousWishlistCount = count;
  };

  const fetchWishlistCardMarkup = async (product) => {
    const sectionId = "section-rendering-wishlist-card";
    const url = new URL(product.url, window.location.origin);
    url.searchParams.set("section_id", sectionId);

    const response = await fetch(url.toString(), { headers: { Accept: "text/html" } });
    if (!response.ok) return "";

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, "text/html");
    const li = doc.querySelector(".wishlist-product-grid__item") || doc.querySelector(".product-grid__item");
    return li ? li.outerHTML : "";
  };

  const variantIdCache = new Map();

  const getHandleFromUrl = (url) => {
    if (!url) return "";
    try {
      const parsed = new URL(url, window.location.origin);
      const match = parsed.pathname.match(/\/products\/([^/?#]+)/);
      return match ? decodeURIComponent(match[1]) : "";
    } catch {
      return "";
    }
  };

  const fetchVariantIdFromProductUrl = async (productUrl) => {
    const handle = getHandleFromUrl(productUrl);
    if (!handle) return "";
    if (variantIdCache.has(handle)) return variantIdCache.get(handle);

    const response = await fetch(`/products/${handle}.js`, {
      headers: { Accept: "application/json" }
    });
    if (!response.ok) return "";

    const productJson = await response.json();
    const variantId = sanitizeId(productJson?.variants?.[0]?.id);
    variantIdCache.set(handle, variantId);
    return variantId;
  };

  const resolveVariantId = async (product) => {
    const existing = sanitizeId(product?.variantId);
    if (existing) return existing;
    return fetchVariantIdFromProductUrl(product?.url);
  };

  const addWishlistItemToCart = async (product) => {
    const variantId = await resolveVariantId(product);
    if (!variantId) {
      window.location.href = product.url;
      return;
    }

    const cartAddUrl = window.Theme?.routes?.cart_add_url || "/cart/add.js";
    const response = await fetch(cartAddUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
    });

    if (!response.ok) throw new Error("Unable to add wishlist item to cart");
  };

  const renderWishlistPage = async () => {
    const grid = document.querySelector("[data-wishlist-grid]");
    if (!grid) return;

    const empty = document.querySelector("[data-wishlist-empty]");
    const state = readState();
    const cards = state.ids
      .map((id) => state.products[id])
      .filter((item) => item && item.id && item.title && item.url);

    if (!cards.length) {
      grid.innerHTML = "";
      if (empty) empty.hidden = false;
      return;
    }

    if (empty) empty.hidden = true;
    const renderedCards = await Promise.all(cards.map((product) => fetchWishlistCardMarkup(product)));
    grid.innerHTML = renderedCards.filter(Boolean).join("");
    updateAllButtons();
  };

  const boot = () => {
    writeState(readState());
    syncProductDataFromEmbeddedJson();
    updateAllButtons();
    updateCount();
    renderWishlistPage();
  };

  document.addEventListener("click", (e) => {
    const btn = e.target.closest("[data-wishlist-toggle]");
    if (!btn) return;
    e.preventDefault();
    const productData = getProductDataFromButton(btn);
    toggle(btn.dataset.productId, productData);
  });

  document.addEventListener("click", async (e) => {
    const btn = e.target.closest("[data-wishlist-add-to-cart]");
    if (!btn) return;

    e.preventDefault();
    if (btn.disabled) return;
    btn.disabled = true;

    try {
      const product = getProductDataFromButton(btn);
      await addWishlistItemToCart(product);
      const label = btn.querySelector("span:last-child");
      if (label) label.textContent = "Added";
      // Remove from wishlist after successful cart add
      if (product && product.id) toggle(product.id);
      // Update cart count in header
      try {
        const cartRes = await fetch("/cart.js", { headers: { Accept: "application/json" } });
        const cartData = await cartRes.json();
        document.dispatchEvent(
          new CustomEvent("cart:update", {
            bubbles: true,
            detail: { resource: cartData, data: { itemCount: cartData.item_count } }
          })
        );
      } catch (_) {}
      setTimeout(() => {
        btn.disabled = false;
      }, 900);
    } catch (error) {
      console.error(error);
      btn.disabled = false;
    }
  });

  const removeCartLine = async (btn) => {
    const line = Number(btn.dataset.cartLine || 0);
    const cartItemsComponent = btn.closest("cart-items-component");

    if (line > 0 && cartItemsComponent && typeof cartItemsComponent.onLineItemRemove === "function") {
      cartItemsComponent.onLineItemRemove(line);
      return { usedComponentApi: true };
    }

    const cartKey = (btn.dataset.cartKey || "").trim();
    if (!cartKey) throw new Error("Missing cart line key");

    const cartChangeUrl = window.Theme?.routes?.cart_change_url || "/cart/change.js";
    const response = await fetch(cartChangeUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ id: cartKey, quantity: 0 })
    });

    if (!response.ok) throw new Error("Unable to remove cart item");
    return { usedComponentApi: false };
  };

  document.addEventListener("click", async (e) => {
    const btn = e.target.closest("[data-move-to-wishlist]");
    if (!btn) return;

    e.preventDefault();
    if (btn.disabled) return;
    btn.disabled = true;

    try {
      const productData = getProductDataFromButton(btn);
      const moved = add(btn.dataset.productId, productData);
      if (!moved) {
        btn.disabled = false;
        return;
      }

      const result = await removeCartLine(btn);
      if (!result.usedComponentApi) {
        window.location.reload();
      }
    } catch (error) {
      console.error(error);
      btn.disabled = false;
    }
  });

  document.addEventListener("DOMContentLoaded", boot);
  document.addEventListener("shopify:section:load", boot);
  document.addEventListener("shopify:section:reorder", boot);
  boot();

  window.FVSWishlist = { read, toggle, add, has, updateAllButtons, updateCount };
})();
